import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Shield, Lock, Key, AlertTriangle } from 'lucide-react';
import { Badge } from '@/components/ui/badge';

export default function SecurityInfo() {
  return (
    <Card className="border-primary/20">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Shield className="w-5 h-5 text-primary" />
          Platform Security Hierarchy
        </CardTitle>
        <CardDescription>
          Understanding access levels and the SaaS security model
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Security Model */}
        <div className="space-y-3">
          <h3 className="font-semibold flex items-center gap-2">
            <Lock className="w-4 h-4" />
            Access Control Model
          </h3>
          <div className="space-y-2 pl-6">
            <div className="p-3 bg-primary/10 border border-primary/20 rounded-lg">
              <div className="flex items-start gap-2">
                <Badge variant="default" className="mt-0.5">Level 1</Badge>
                <div>
                  <p className="font-semibold text-primary">Platform Owner</p>
                  <p className="text-sm text-muted-foreground">
                    You - Full system access including Agent Control Panel. 
                    Verified by database check: <code className="text-xs bg-muted px-1 rounded">user_roles.role = 'owner' AND org_id IS NULL</code>
                  </p>
                </div>
              </div>
            </div>

            <div className="p-3 bg-muted/50 rounded-lg">
              <div className="flex items-start gap-2">
                <Badge variant="secondary" className="mt-0.5">Level 2</Badge>
                <div>
                  <p className="font-semibold">Organization Owner</p>
                  <p className="text-sm text-muted-foreground">
                    Customers who own organizations. Access to their org data and settings. 
                    Check: <code className="text-xs bg-muted px-1 rounded">user_roles.role = 'owner' AND org_id IS NOT NULL</code>
                  </p>
                </div>
              </div>
            </div>

            <div className="p-3 bg-muted/50 rounded-lg">
              <div className="flex items-start gap-2">
                <Badge variant="outline" className="mt-0.5">Level 3</Badge>
                <div>
                  <p className="font-semibold">Organization Members</p>
                  <p className="text-sm text-muted-foreground">
                    Roles: Admin, Manager, Rep, Client. Limited access based on role within their organization.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Security Implementation */}
        <div className="space-y-3">
          <h3 className="font-semibold flex items-center gap-2">
            <Key className="w-4 h-4" />
            How It's Protected
          </h3>
          <div className="pl-6 space-y-2 text-sm">
            <div className="flex items-start gap-2">
              <Shield className="w-4 h-4 text-green-500 mt-0.5" />
              <div>
                <p className="font-medium">Server-Side Validation</p>
                <p className="text-muted-foreground">Every request checks the database for platform owner status. Cannot be bypassed.</p>
              </div>
            </div>
            
            <div className="flex items-start gap-2">
              <Shield className="w-4 h-4 text-green-500 mt-0.5" />
              <div>
                <p className="font-medium">Row Level Security (RLS)</p>
                <p className="text-muted-foreground">Database enforces access policies at the data layer.</p>
              </div>
            </div>

            <div className="flex items-start gap-2">
              <Shield className="w-4 h-4 text-green-500 mt-0.5" />
              <div>
                <p className="font-medium">Role-Based Access Control</p>
                <p className="text-muted-foreground">Stored in <code className="bg-muted px-1 rounded">user_roles</code> table, separate from user profiles.</p>
              </div>
            </div>
          </div>
        </div>

        {/* Warning */}
        <div className="p-4 bg-destructive/10 border border-destructive/20 rounded-lg">
          <div className="flex items-start gap-3">
            <AlertTriangle className="w-5 h-5 text-destructive mt-0.5" />
            <div className="space-y-2">
              <p className="font-semibold text-destructive">Critical Security Notice</p>
              <p className="text-sm text-muted-foreground">
                The Agent Control Panel is protected by database-level authentication. 
                Only users with platform owner status can access these controls. 
                Organization owners and members will be redirected if they attempt to access this page.
              </p>
              <div className="mt-2 p-2 bg-background rounded text-xs font-mono">
                Protected routes: /agent-control
              </div>
            </div>
          </div>
        </div>

        {/* Current Status */}
        <div className="p-3 bg-primary/5 rounded-lg border border-primary/10">
          <p className="text-sm">
            <strong className="text-primary">Your Current Status:</strong> Platform Owner
          </p>
          <p className="text-xs text-muted-foreground mt-1">
            You have full access to all agent configuration tools and platform administration features.
          </p>
        </div>
      </CardContent>
    </Card>
  );
}
